import React from 'react';
import { X, CheckCircle, XCircle, ArrowUpCircle, ArrowDownCircle, RefreshCw, Loader, Clock, Play, Pause, Cloud, AlertTriangle } from 'lucide-react';
import { useSync } from '../../context/SyncContext';
import './SyncModal.css';

const SyncModal = () => {
  const {
    showModal, setShowModal,
    active, interval, countdown, syncing,
    lastResult, syncHistory, syncConfig,
    activate, deactivate, manualSync, setIntervalMin
  } = useSync();

  if (!showModal) return null;

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch { return dateStr; }
  };

  const getResultSummary = (result) => {
    if (!result) return '';
    if (!result.success) return result.message || 'Error';
    const push = result.data?.push?.resumen || result.data?.resumen || {};
    const pull = result.data?.pull?.resumen || {};
    const parts = [];
    if (push.total_enviados > 0) parts.push(`${push.total_enviados} enviados`);
    if (pull.total_recibidos > 0) parts.push(`${pull.total_recibidos} recibidos`);
    if (parts.length === 0) parts.push('Sin cambios');
    const dur = result.data?.push?.resumen?.duracion_total_ms || result.data?.resumen?.duracion_total_ms || 0;
    if (dur > 0) parts.push(`${(dur / 1000).toFixed(1)}s`);
    return parts.join(' | ');
  };

  return (
    <div className="sync-modal-overlay" onClick={() => setShowModal(false)}>
      <div className="sync-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sync-modal-header">
          <div className="sync-modal-title">
            <Cloud size={20} />
            <span>Sincronización con Nube</span>
          </div>
          <button className="sync-modal-close" onClick={() => setShowModal(false)}>
            <X size={18} />
          </button>
        </div>

        {/* Estado actual */}
        <div className="sync-modal-body">
          {!syncConfig ? (
            <div className="sync-modal-noconfig">
              <AlertTriangle size={32} color="#f59e0b" />
              <p>Sincronización no configurada.</p>
              <p style={{ fontSize: '12px', color: '#6b7280' }}>Ve a Configuración &gt; Admin &gt; Sincronización con Nube para configurar URL, negocio y sucursal.</p>
            </div>
          ) : (
            <>
              {/* Info de conexión */}
              <div className="sync-modal-info">
                <span className="sync-info-label">Servidor:</span>
                <span className="sync-info-value" title={syncConfig.url}>
                  {syncConfig.url.length > 40 ? '...' + syncConfig.url.slice(-37) : syncConfig.url}
                </span>
                <span className="sync-info-label">Sucursal:</span>
                <span className="sync-info-value">{syncConfig.sucursal}</span>
              </div>

              {/* Botones de acción */}
              <div className="sync-modal-actions">
                <button
                  className="sync-btn sync-btn-push"
                  disabled={syncing}
                  onClick={() => manualSync('push')}
                >
                  {syncing ? <Loader size={14} className="spinning" /> : <ArrowUpCircle size={14} />}
                  Push
                </button>
                <button
                  className="sync-btn sync-btn-pull"
                  disabled={syncing}
                  onClick={() => manualSync('pull')}
                >
                  {syncing ? <Loader size={14} className="spinning" /> : <ArrowDownCircle size={14} />}
                  Pull
                </button>
                <button
                  className="sync-btn sync-btn-full"
                  disabled={syncing}
                  onClick={() => manualSync('full')}
                >
                  {syncing ? <Loader size={14} className="spinning" /> : <RefreshCw size={14} />}
                  Sync Completo
                </button>
              </div>

              {/* Timer automático */}
              <div className="sync-modal-timer">
                <div className="sync-timer-header">
                  <Clock size={14} />
                  <span>Sync Automático</span>
                </div>
                <div className="sync-timer-controls">
                  <select
                    value={interval}
                    onChange={e => setIntervalMin(parseInt(e.target.value, 10))}
                    disabled={active}
                    className="sync-timer-select"
                  >
                    <option value={1}>1 min</option>
                    <option value={2}>2 min</option>
                    <option value={5}>5 min</option>
                    <option value={10}>10 min</option>
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                    <option value={60}>1 hora</option>
                  </select>
                  {!active ? (
                    <button className="sync-btn sync-btn-activate" onClick={() => activate(interval)}>
                      <Play size={14} /> Activar
                    </button>
                  ) : (
                    <button className="sync-btn sync-btn-deactivate" onClick={deactivate}>
                      <Pause size={14} /> Detener
                    </button>
                  )}
                  {active && (
                    <span className="sync-timer-countdown">
                      Próximo: {formatCountdown(countdown)}
                    </span>
                  )}
                </div>
              </div>

              {/* Último resultado */}
              {lastResult && (
                <div className={`sync-modal-result ${lastResult.success ? 'success' : 'error'}`}>
                  {lastResult.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                  <span>{getResultSummary(lastResult)}</span>
                </div>
              )}

              {/* Historial */}
              {syncHistory.length > 0 && (
                <div className="sync-modal-history">
                  <div className="sync-history-title">Historial</div>
                  <div className="sync-history-list">
                    {syncHistory.map((entry, idx) => (
                      <div key={idx} className={`sync-history-item ${entry.success ? '' : 'error'}`}>
                        <span className="sync-history-time">{formatTime(entry.timestamp)}</span>
                        <span className="sync-history-icon">
                          {entry.success ? <CheckCircle size={12} color="#10b981" /> : <XCircle size={12} color="#ef4444" />}
                        </span>
                        <span className="sync-history-detail">{getResultSummary(entry)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncModal;
